import { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useAuth } from '@/auth';
import { type ChatAttachmentRef } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useChat } from '../hooks/useChat';
import { useChatChannel } from '../hooks/useChatChannel';
import { useChannelMessagePermissions } from '../hooks/useChannelMessagePermissions';
import { useMessageReactions } from '../hooks/useMessageReactions';
import { MessageList, type MessageListHandle } from './MessageList';
import { ChannelSearchPanel } from './ChannelSearchPanel';
import { TypingIndicator } from './TypingIndicator';
import type { MessageInputHandle } from './MessageInput';

// Same code-split as ChatPage: MessageInput pulls in TipTap/ProseMirror
// (~150 KB). Importing it eagerly here would drag that into the guilds bundle.
const MessageInput = lazy(() =>
  import('./MessageInput').then((m) => ({ default: m.MessageInput })),
);

interface EmbeddedChannelChatProps {
  channelId: string;
  /** Shown in the composer placeholder, e.g. the channel name. */
  channelLabel?: string;
}

/**
 * A self-contained conversation pane for ONE channel — message history, live
 * updates, and the composer — for embedding outside the chat arche.
 *
 * It drives the same `useChat` hook ChatPage uses, so it joins the same
 * WebSocket subscription for the same `chat_channels` row. That's what makes a
 * message sent here appear in the chat arche (and vice versa) without any
 * cross-arche syncing: there is only one channel, and both surfaces are live
 * views onto it.
 *
 * Deliberately NOT a copy of ChatPage: no channel list, no settings, no
 * preview/DM plumbing. Those belong to the full arche.
 */
export function EmbeddedChannelChat({ channelId, channelLabel }: EmbeddedChannelChatProps) {
  const { user } = useAuth();
  const currentUserId = user?.userId || null;
  const isAdmin = user?.roles?.includes('admin') ?? false;

  // No autoJoinGlobal: rendering an embedded pane (e.g. on a guild page) must
  // never silently enroll the user in the global community channel — that side
  // effect belongs to opening the chat arche itself.
  const { messages, typing } = useChat(channelId, { autoJoinGlobal: false });
  const { channel, members, memberCount } = useChatChannel(channelId);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const messageInputRef = useRef<MessageInputHandle>(null);
  const messageListRef = useRef<MessageListHandle>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleJumpToMessage = useCallback((messageId: string) => {
    messageListRef.current?.jumpToMessage(messageId);
  }, []);

  const [editingMessage, setEditingMessage] = useState<{
    messageId: string;
    content: string;
  } | null>(null);
  const [replyTo, setReplyTo] = useState<{
    messageId: string;
    senderName: string;
    content: string;
  } | null>(null);

  const handleSend = useCallback(
    async (
      content: string,
      replyToId?: string,
      attachmentRefs?: ChatAttachmentRef[],
    ): Promise<boolean> => {
      // sendMessage resolves to the created message (or null on failure);
      // MessageInput only needs to know whether to clear the composer.
      const sent = await messagesRef.current.sendMessage(content, replyToId, attachmentRefs);
      if (sent) setReplyTo(null);
      return !!sent;
    },
    [],
  );

  const handleStartEdit = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (msg) setEditingMessage({ messageId, content: msg.content });
  }, []);

  const handleSaveEdit = useCallback(async (messageId: string, content: string) => {
    await messagesRef.current.editMessage(messageId, content);
    setEditingMessage(null);
  }, []);

  const handleDelete = useCallback((messageId: string) => {
    messagesRef.current.deleteMessage(messageId);
  }, []);

  const handleReaction = useMessageReactions(messagesRef, currentUserId);

  const handleReply = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (!msg) return;
    setReplyTo({ messageId, senderName: msg.senderName || 'Unknown', content: msg.content });
    messageInputRef.current?.focus();
  }, []);

  const memberNameById = new Map(members.map((m) => [m.userId, m.displayName || m.userId]));
  const hasLeft = !!channel?.hasLeft;

  // Effective channel-level composer permissions — same resolution as ChatPage,
  // so a read-only channel is read-only here too (MessageInput defaults every
  // permission to allowed when the prop is omitted).
  const currentChannelMember = members.find((m) => m.userId === currentUserId) ?? null;
  const messagePermissions = useChannelMessagePermissions(channel, currentChannelMember);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* The pane deliberately carries no channel chrome (no list, no settings —
          those belong to the arche). Search is a READING affordance for the
          history already shown here, not channel management, so it gets a single
          icon rather than a header bar. */}
      <div className="flex justify-end px-2 py-1 border-b border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSearchOpen(true)}
          className="h-7 w-7"
          title="Search messages"
          aria-label="Search messages"
        >
          <Search className="h-3.5 w-3.5" />
        </Button>
      </div>
      {searchOpen && (
        <ChannelSearchPanel
          channelId={channelId}
          channelLabel={channelLabel || 'this conversation'}
          members={members}
          open
          onOpenChange={setSearchOpen}
          onSelectResult={handleJumpToMessage}
        />
      )}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <MessageList
          ref={messageListRef}
          messages={messages.messages}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          loading={messages.loading}
          loadingMore={messages.loadingMore}
          hasMore={messages.hasMore}
          onLoadMore={messages.loadMore}
          onStartEditMessage={handleStartEdit}
          onDeleteMessage={handleDelete}
          onReplyMessage={handleReply}
          onReactionMessage={handleReaction}
        />
      </div>

      <TypingIndicator
        typingUsers={typing.typingUsers
          .filter((u) => u.userId !== currentUserId)
          .map((u) => ({ ...u, displayName: memberNameById.get(u.userId) ?? '' }))}
      />

      <Suspense
        fallback={
          <div className="border-t border-border p-3 text-xs text-muted-foreground">
            Loading composer…
          </div>
        }
      >
        <MessageInput
          ref={messageInputRef}
          channelId={channelId}
          onSend={handleSend}
          onSaveEdit={handleSaveEdit}
          onDeleteMessage={handleDelete}
          onTyping={typing.sendTyping}
          members={members}
          memberCount={memberCount}
          currentUserId={currentUserId}
          permissions={messagePermissions}
          disabled={hasLeft}
          leftChannel={hasLeft}
          placeholder={
            hasLeft
              ? 'You left this channel'
              : `Message ${channelLabel || channel?.name || 'channel'}...`
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editingMessage={editingMessage}
          onCancelEdit={() => setEditingMessage(null)}
        />
      </Suspense>
    </div>
  );
}
